import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { Alert, Icon, Intent, Popover, Position, Spinner } from '@blueprintjs/core';
import moment from 'moment';
import Toast from 'helpers/toaster';
import { currencyFormattedAmount } from 'helpers/currency';
import { materialStatus, MATERIAL_STATUSES } from 'helpers/materialStatus';
import { GrowingTextArea } from 'components/FormBits';
import ProductServiceAutocomplete from 'components/ProductServiceAutocomplete';
import SupplierAutocomplete from 'components/SupplierAutocomplete';
import DateTimeInput from 'components/DateTimeInput';
import { amount, figures, setQuantity, setRate, setMarkup, restoreDefaultRates, isTruckCharge } from 'components/DocEditor/lineMath';
import { reason } from 'components/DocEditor/DocModel';
import UnitPicker from 'components/DocEditor/UnitPicker';
import TaxPicker from 'components/DocEditor/TaxPicker';

// The line items of a work order, estimate or invoice, edited in place like a
// spreadsheet: one group each for labour, materials and other lines; a row is
// the item on top (its name, the numbers, the total) with its details under
// it. Every edit goes into the record and is sent with Save; totals follow as
// you type.

export const GROUPS = [
  { key: 'labour', kind: 'labour', icon: 'time', title: 'Labor', one: 'labor line', add: 'Add labor', qty: 'Hours', rate: 'Rate', type: 'Service' },
  { key: 'materials', kind: 'material', icon: 'box', title: 'Materials', one: 'material', add: 'Add material', qty: 'Qty', rate: 'Unit price', type: 'Products (Non-inventory)' },
  { key: 'custom_line_items', kind: 'custom', icon: 'tag', title: 'Other', one: 'line', add: 'Add line', qty: 'Qty', rate: 'Rate' },
];

const money = (value) => currencyFormattedAmount(amount(value));

// A number field that keeps what's typed ("1." on the way to "1.5") while the
// record holds the parsed value.
@observer
class NumberCell extends React.Component {
  @observable draft = null;

  render() {
    const { value, onChange, label, readOnly, prefix, decimals } = this.props;
    const blank = value === null || value === undefined || value === '' || Number.isNaN(value);
    const number = parseFloat(value);
    const resting = blank ? '' : (decimals && Number.isFinite(number)
      ? number.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      : String(value));
    const shown = this.draft !== null ? this.draft : resting;
    return (
      <span className={`de-num${prefix ? ' de-num--prefixed' : ''}${readOnly ? ' is-readonly' : ''}`}>
        {prefix && <span className="de-num__prefix" aria-hidden="true">{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          aria-label={label}
          value={shown}
          readOnly={readOnly}
          onFocus={(event) => { this.draft = blank ? '' : String(value); const input = event.target; setTimeout(() => input.select(), 0); }}
          onBlur={() => { this.draft = null; }}
          onChange={(event) => { this.draft = event.target.value; onChange(event.target.value); }}
        />
      </span>
    );
  }
}

// A material's order on a work order: send it to Material orders, or see
// where it's at.
@observer
class MaterialOrder extends React.Component {
  @observable problem = null;
  @observable busy = false;

  missing() {
    const { item } = this.props;
    const gaps = [];
    if (!item.description) gaps.push('a description');
    if (!item.supplier_id) gaps.push('a supplier');
    if (!item.part_number) gaps.push('a part number');
    if (!amount(item.quantity)) gaps.push('a quantity');
    if (!amount(item.rate_per_unit)) gaps.push('a unit price');
    return gaps;
  }

  order = () => {
    const gaps = this.missing();
    if (gaps.length) {
      this.problem = `To add it to the ordering queue, add ${gaps.length > 1 ? `${gaps.slice(0, -1).join(', ')} and ${gaps[gaps.length - 1]}` : gaps[0]}.`;
      return;
    }
    this.problem = null;
    this.busy = true;
    const { doc, item } = this.props;
    doc.orderMaterial(item)
      .then(() => { Toast.show({ message: `${item.description || 'The material'} added to the ordering queue.`, intent: Intent.SUCCESS }); })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't add it to the ordering queue. Try again."), intent: Intent.DANGER }); })
      .finally(action(() => { this.busy = false; }));
  };

  // Where the order is: its status along the steps, what was ordered, the
  // purchase order and the supplier.
  renderOrders() {
    const { doc } = this.props;
    const { isLoading, objects } = doc.materialOrders();
    const orders = objects || [];
    if (isLoading && orders.length === 0) {
      return <div className="mo-card mo-card--loading" role="status"><Spinner size={18} /> Loading the order…</div>;
    }
    if (orders.length === 0) {
      return <div className="mo-card"><p className="mo-empty">This order couldn't be found. Open Material orders to check it.</p></div>;
    }
    return (
      <div className="mo-card">
        {orders.map((order) => {
          const a = order.attributes;
          const status = materialStatus(a.status);
          const archived = a.status === 'archived';
          const current = MATERIAL_STATUSES.findIndex((step) => step.value === a.status);
          return (
            <section key={order.id} className="mo-order" aria-label={`Order: ${a.description || 'material'}`}>
              <header className="mo-order__head">
                <span className="mo-order__title">Material order</span>
                <span className={`dl-pill dl-pill--${status.tone}`}>{status.label}</span>
              </header>
              {!archived && (
                <ol className="mo-track" aria-label="Where it's at">
                  {MATERIAL_STATUSES.map((step, index) => {
                    const state = index < current ? 'is-done' : index === current ? 'is-current' : '';
                    return (
                      <li key={step.value} className={`mo-step ${state}`} aria-current={index === current ? 'step' : undefined}>
                        <span className="mo-step__dot" aria-hidden="true">{index < current && <Icon icon="tick" iconSize={8} />}</span>
                        <span className="mo-step__label">{step.label}</span>
                      </li>
                    );
                  })}
                </ol>
              )}
              <dl className="mo-facts">
                <div><dt>Ordered</dt><dd>{a.description || 'Material'}{a.quantity ? <span className="mo-qty"> × {parseFloat(a.quantity)}</span> : null}</dd></div>
                <div>
                  <dt>Purchase order</dt>
                  <dd>{a.purchase_order
                    ? <a href={`/dashboard/purchase-orders/${a.purchase_order.id}/show-purchase-order`}>{a.purchase_order.purchase_order_number}</a>
                    : <span className="mo-muted">Not on a purchase order yet</span>}</dd>
                </div>
                <div>
                  <dt>Supplier</dt>
                  <dd>{a.supplier ? <a href={`/dashboard/suppliers/${a.supplier.id}/show`}>{a.supplier.name}</a> : <span className="mo-muted">None</span>}</dd>
                </div>
              </dl>
            </section>
          );
        })}
        <footer className="mo-card__foot">
          <a href="/dashboard/materialorders">Open material orders</a>
        </footer>
      </div>
    );
  }

  render() {
    const { doc, item } = this.props;
    const orders = item.material_line_items || [];
    if (orders.length === 0) {
      return (
        <span className="de-order">
          <button type="button" className="de-link-button" disabled={this.busy} onClick={this.order}>
            <Icon icon={this.busy ? 'time' : 'shopping-cart'} iconSize={12} /> {this.busy ? 'Adding…' : 'Add to ordering queue'}
          </button>
          {this.problem && <span className="de-order__problem" role="alert">{this.problem}</span>}
        </span>
      );
    }
    const status = materialStatus(orders[0].status);
    return (
      <Popover
        position={Position.BOTTOM_RIGHT}
        minimal
        popoverClassName="mo-popover"
        autoFocus={false}
        enforceFocus={false}
        onOpening={() => doc.loadMaterialOrders(item)}
        content={this.renderOrders()}
      >
        <button type="button" className={`mo-pill dl-pill dl-pill--${status.tone}`} aria-label={`Order: ${status.label}. Show the order`}>
          {status.label}
          <Icon icon="chevron-down" iconSize={10} />
        </button>
      </Popover>
    );
  }
}

// A line keeps at least one tax (the server won't take off the last one).
const LAST_TAX = 'A line keeps at least one tax. To stop taxing it, pick a 0% tax instead.';

// A line's taxes (estimates, invoices): which tax, its amount, and (where the
// record allows it) taking it off; not the last one, which can only be changed.
@observer
class TaxLines extends React.Component {
  @observable deleting = null;
  @observable busy = null;

  change(taxLine, taxId) {
    const { doc } = this.props;
    const tax = doc.taxes().find((option) => option.id === taxId || (option.attributes && option.attributes.id === taxId));
    if (!tax) return;
    this.busy = taxLine.id;
    doc.setTax(taxLine, tax)
      .then(() => { doc.recalc(); })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't change the tax. Try again."), intent: Intent.DANGER }); })
      .finally(action(() => { this.busy = null; }));
  }

  confirmDelete = () => {
    const { doc, group } = this.props;
    const taxLine = this.deleting;
    this.deleting = null;
    doc.deleteTax(taxLine, group.kind)
      .then(() => { Toast.show({ message: 'Tax taken off the line.', intent: Intent.SUCCESS }); })
      .catch((error) => {
        const message = reason(error, "Couldn't take the tax off. Try again.");
        Toast.show({ message: /last tax/i.test(message) ? LAST_TAX : message, intent: Intent.DANGER });
      });
  };

  render() {
    const { doc, item, group } = this.props;
    const taxes = doc.taxes();
    const lines = item.tax_line_items || [];
    const canDelete = doc.canDeleteTax(group.kind) && lines.length > 1;
    return (
      <span className="de-taxes">
        {lines.map((taxLine) => {
          const tax = taxes.find((option) => option.id === taxLine.tax_id);
          const name = (tax && tax.attributes.name) || taxLine.name;
          const label = name ? `${name} ${taxLine.percentage_value}%` : `Tax ${taxLine.percentage_value}%`;
          return (
            <TaxPicker
              key={taxLine.id}
              taxLine={taxLine}
              label={label}
              taxes={taxes}
              busy={this.busy === taxLine.id}
              canDelete={canDelete}
              lastTaxNote={LAST_TAX}
              amountText={money((amount(taxLine.percentage_value) / 100) * amount(item.total))}
              onPick={(taxId) => this.change(taxLine, taxId)}
              onDelete={() => { this.deleting = taxLine; }}
            />
          );
        })}
        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Take tax off"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>Take this tax off the line?</strong>
          <p>The line won't be taxed any more{doc.dirty ? ', and your other changes are saved with it' : ''}.</p>
        </Alert>
      </span>
    );
  }
}

// A group's figures under its lines (before markup, gross markup and margin,
// subtotal); its own observer so a keystroke only updates the figures.
const GroupFigures = observer(({ items, markup }) => {
  if (items.length === 0) return null;
  const sums = figures(items);
  return (
    <dl className="de-group__figures">
      {markup && (
        <>
          <div><dt>Before markup</dt><dd>{money(sums.cost)}</dd></div>
          <div><dt>Gross markup</dt><dd>{sums.markupPercent === null ? '–' : `${sums.markupPercent}%`}</dd></div>
          <div><dt>Gross margin</dt><dd>{money(sums.margin)}</dd></div>
        </>
      )}
      <div className="is-strong"><dt>Subtotal</dt><dd>{money(sums.subtotal)}</dd></div>
    </dl>
  );
});

// One line item.
@observer
class LineRow extends React.Component {
  @observable pending = null; // price book values that differ from the line's, offered
  edit(apply) {
    apply(this.props.item);
    this.props.doc.recalc();
  }

  // Picking from the price book fills what's still empty on the line. Where
  // the line already has something different (its own description, rate…),
  // it asks first: the differences show under the line with Update / Keep
  // mine.
  selectProduct = (product) => {
    const { doc, group, item } = this.props;
    item.product_service_id = product ? product.id : null;
    item.product_service = product || null;
    this.pending = null;
    if (!product) return;
    const p = product.attributes || {};
    const changes = [];
    // A field from the price book: fill it if empty, otherwise offer it.
    const offer = (label, mine, theirs, apply, { show = (value) => value } = {}) => {
      if (theirs === null || theirs === undefined || theirs === '') return;
      const empty = mine === null || mine === undefined || mine === '' || mine === 0;
      if (empty) {
        apply();
      } else if (String(show(mine)) !== String(show(theirs))) {
        changes.push({ label, mine: show(mine), theirs: show(theirs), apply });
      }
    };
    offer('Description', item.description, p.description, () => { item.description = p.description; });
    if (group.kind === 'material') {
      offer('Part no.', item.part_number, p.part_number, () => { item.part_number = p.part_number; });
      if (p.supplier_id) {
        const supplierName = (item.supplier && (item.supplier.name || (item.supplier.attributes && item.supplier.attributes.name))) || '';
        offer('Supplier', item.supplier_id ? supplierName || 'Another supplier' : null, p.supplier_name || (p.supplier && p.supplier.name) || 'Price book supplier', () => {
          item.supplier_id = p.supplier_id;
          item.supplier = p.supplier || (p.supplier_name ? { id: p.supplier_id, name: p.supplier_name } : null);
        });
      }
    }
    if (p.unit_price !== null && p.unit_price !== undefined && p.unit_price !== '') {
      const markup = parseFloat(p.mark_up);
      const hasMarkup = Number.isFinite(markup) && markup > 0;
      // No markup column (invoices): the markup goes into the price, as the catalog does.
      const rate = doc.features.markup || !hasMarkup ? parseFloat(p.unit_price) : parseFloat((parseFloat(p.unit_price) * markup).toFixed(2));
      const priced = Boolean(amount(item.rate_per_unit));
      offer(group.rate, amount(item.rate_per_unit), rate, () => { setRate(item, String(rate)); }, { show: (value) => money(value) });
      // The markup comes with the price: filled on a line with no price yet,
      // otherwise offered with it (a markup of 1 is still the line's markup).
      if (doc.features.markup && hasMarkup) {
        offer('Markup', priced ? (item.markup || 1) : null, markup, () => { item.markup = markup; }, { show: (value) => `×${parseFloat(value)}` });
      }
    }
    if (!amount(item.quantity)) setQuantity(item, '1');
    doc.recalc();
    if (changes.length > 0) this.pending = { name: p.name, changes };
  };

  applyPending = () => {
    this.pending.changes.forEach((change) => change.apply());
    this.pending = null;
    this.props.doc.recalc();
  };

  renderPending() {
    const { name, changes } = this.pending;
    const what = changes.map((change) => change.label.toLowerCase());
    const list = what.length > 1 ? `${what.slice(0, -1).join(', ')} and ${what[what.length - 1]}` : what[0];
    return (
      <div className="de-offer" role="status">
        <p className="de-offer__text"><strong>{name}</strong> has a different {list} in your price book. Update this line?</p>
        <dl className="de-offer__changes">
          {changes.map((change) => (
            <div key={change.label}>
              <dt>{change.label}</dt>
              <dd><span className="de-offer__mine">{change.mine}</span><Icon icon="arrow-right" iconSize={11} /><span className="de-offer__theirs">{change.theirs}</span></dd>
            </div>
          ))}
        </dl>
        <div className="de-offer__actions">
          <button type="button" className="de-offer__update" onClick={this.applyPending}>Update</button>
          <button type="button" className="de-offer__keep" onClick={() => { this.pending = null; }}>Keep mine</button>
        </div>
      </div>
    );
  }

  selectSupplier = (supplier) => {
    const { item } = this.props;
    item.supplier_id = supplier ? supplier.id : null;
    item.supplier = supplier || null;
  };

  toggleOverride = (event) => {
    const { doc, item } = this.props;
    item.override = event.target.checked;
    if (!item.override) restoreDefaultRates(doc.attributes, { truckCharge: doc.truck });
    doc.recalc();
  };

  renderMain() {
    const { group, item, index } = this.props;
    if (group.kind === 'custom') {
      return (
        <input
          className="de-text de-text--strong"
          aria-label={`Description of line ${index + 1}`}
          placeholder="What's this charge for?"
          value={item.description || ''}
          onChange={(event) => this.edit((row) => { row.description = event.target.value; })}
        />
      );
    }
    return (
      <div className="de-product">
        <ProductServiceAutocomplete
          kind={group.kind}
          value={item.product_service}
          onProductSelect={this.selectProduct}
          inputProps={{ 'aria-label': group.kind === 'labour' ? 'Service' : 'Product' }}
        />
      </div>
    );
  }

  renderDetails() {
    const { doc, group, item } = this.props;
    const features = doc.features;
    const details = [];
    if (group.kind === 'labour') {
      details.push(
        <label key="date" className="de-detail de-detail--date">
          <span>Date</span>
          <DateTimeInput
            format="MMM D, YYYY h:mm A"
            defaultValue={item.work_date ? moment(item.work_date) : undefined}
            onChange={(date) => this.edit((row) => { row.work_date = moment(date); })}
          />
        </label>,
      );
    }
    if (group.kind === 'material') {
      details.push(
        <label key="supplier" className="de-detail de-detail--supplier">
          <span>Supplier</span>
          <SupplierAutocomplete value={item.supplier} onSupplierSelect={this.selectSupplier} inputProps={{ placeholder: 'Choose a supplier' }} />
        </label>,
        <label key="part" className="de-detail">
          <span>Part no.</span>
          <input className="de-text" aria-label="Part number" value={item.part_number || ''} placeholder="Add" onChange={(event) => { item.part_number = event.target.value; }} />
        </label>,
        <div key="unit" className="de-detail">
          <span>Unit</span>
          <UnitPicker value={item.unit} onChange={(unit) => { item.unit = unit; }} />
        </div>,
      );
      if (features.materialOrders) details.push(<span key="order" className="de-detail de-detail--order"><MaterialOrder doc={doc} item={item} /></span>);
    }
    if (group.kind === 'custom' && features.override && item.default_line_item_id) {
      details.push(
        <label key="override" className="de-detail de-override">
          <input type="checkbox" checked={Boolean(item.override)} onChange={this.toggleOverride} />
          <span>Set the rate myself</span>
        </label>,
      );
    }
    const taxes = features.taxes ? (item.tax_line_items || []) : [];
    if (taxes.length > 0) details.push(<TaxLines key="taxes" doc={doc} item={item} group={group} />);
    const ordering = group.kind === 'material' && features.materialOrders ? ' has-order' : '';
    return details.length > 0 ? <div className={`de-details de-details--${group.kind}${ordering}`}>{details}</div> : null;
  }

  render() {
    const { doc, group, item, index, onDelete } = this.props;
    const features = doc.features;
    const truck = group.kind === 'custom' && isTruckCharge(item, doc.truck);
    const rateLocked = group.kind === 'custom' && features.override && item.default_line_item_id && !item.override;
    const markupLocked = group.kind === 'custom' && Boolean(doc.truck && doc.truck.enabled);
    const name = item.description || (item.product_service && (item.product_service.name || (item.product_service.attributes && item.product_service.attributes.name))) || `${group.one} ${index + 1}`;
    return (
      <div className={`de-row${features.markup ? ' has-markup' : ''}${index % 2 === 1 ? ' is-alt' : ''}`} role="row">
        <div className="de-row__main" role="cell">
          {this.renderMain()}
          {group.kind !== 'custom' && (
            <GrowingTextArea
              className="de-text de-text--note"
              rows={1}
              aria-label={`Description of ${name}`}
              placeholder="Add a description"
              value={item.description || ''}
              onChange={(event) => { item.description = event.target.value; }}
            />
          )}
          {this.pending && this.renderPending()}
          {group.kind !== 'material' && this.renderDetails()}
          {truck && <p className="de-hint">Truck charge: billed per quarter hour at the company rate.</p>}
        </div>
        <div className="de-row__cell" role="cell" data-label={group.qty}>
          <NumberCell label={`${group.qty} for ${name}`} value={item.quantity_display_value !== undefined && item.quantity_display_value !== null ? item.quantity_display_value : item.quantity} onChange={(raw) => this.edit((row) => setQuantity(row, raw))} />
        </div>
        <div className="de-row__cell" role="cell" data-label={group.rate}>
          <NumberCell
            decimals={2}
            label={`${group.rate} for ${name}`}
            value={item.rate_per_unit}
            readOnly={rateLocked}
            onChange={(raw) => this.edit((row) => setRate(row, raw))}
          />
        </div>
        {features.markup && (
          <div className="de-row__cell" role="cell" data-label="Markup">
            <NumberCell
              label={`Markup for ${name}`}
              value={item.markup}
              readOnly={markupLocked}
              onChange={(raw) => this.edit((row) => setMarkup(row, raw, { flagOverride: group.kind === 'material' }))}
            />
          </div>
        )}
        <div className="de-row__total" role="cell" data-label="Total">
          {money(item.total)}
        </div>
        {group.kind === 'material' && <div className="de-row__details" role="cell">{this.renderDetails()}</div>}
        <div className="de-row__actions" role="cell">
          <button type="button" className="de-icon-button de-icon-button--danger" aria-label={`Delete ${name}`} title="Delete" onClick={() => onDelete(item, group)}>
            <Icon icon="trash" iconSize={14} />
          </button>
        </div>
      </div>
    );
  }
}

@observer
class LineItems extends React.Component {
  @observable deleting = null; // { item, group }
  @observable deleteTimeLog = false;
  @observable adding = null; // group key while a blank line is added

  askDelete = (item, group) => {
    this.deleteTimeLog = Boolean(item.time_log_id) && item.time_log_status !== 'approved';
    this.deleting = { item, group };
  };

  confirmDelete = () => {
    const { item, group } = this.deleting;
    const timeLog = group.kind === 'labour' ? this.deleteTimeLog : null;
    this.deleting = null;
    const name = item.description || `The ${group.one}`;
    this.props.doc.deleteLine(item, timeLog)
      .then(() => { Toast.show({ message: `${name} deleted.`, intent: Intent.SUCCESS }); })
      .catch((error) => { Toast.show({ message: reason(error, `Couldn't delete the ${group.one}. Try again.`), intent: Intent.DANGER }); });
  };

  add(group) {
    const { doc } = this.props;
    if (group.type) {
      doc.openPicker(group.type);
      return;
    }
    this.adding = group.key;
    doc.addBlankLine(group.kind)
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't add a line. Try again."), intent: Intent.DANGER }); })
      .finally(action(() => { this.adding = null; }));
  }

  renderGroup(group) {
    const { doc } = this.props;
    const items = doc.attributes[group.key] || [];
    const features = doc.features;
    return (
      <section key={group.key} className={`de-group de-group--${group.kind}`} aria-labelledby={`de-group-${group.key}`}>
        <div className={`de-row de-row--head${features.markup ? ' has-markup' : ''}`} role="row">
          <h3 className="de-group__title" id={`de-group-${group.key}`} role="columnheader">
            <span className={`de-group__icon de-part--${group.key}`} aria-hidden="true"><Icon icon={group.icon} iconSize={13} /></span>
            {group.title}
            {items.length > 0 && <span className="de-group__count">{items.length}</span>}
          </h3>
          <span role="columnheader">{group.qty}</span>
          <span role="columnheader">{group.rate}</span>
          {features.markup && <span role="columnheader">Markup</span>}
          <span role="columnheader">Total</span>
        </div>
        {items.map((item, index) => (
          <LineRow key={item.id || index} doc={doc} group={group} item={item} index={index} onDelete={this.askDelete} />
        ))}
        <div className="de-group__foot">
          <button type="button" className="de-add" disabled={this.adding === group.key} onClick={() => this.add(group)}>
            <Icon icon={this.adding === group.key ? 'time' : 'plus'} iconSize={12} /> {this.adding === group.key ? 'Adding…' : group.add}
          </button>
          <GroupFigures items={items} markup={features.markup} />
        </div>
      </section>
    );
  }

  render() {
    const deleting = this.deleting;
    const labour = deleting && deleting.group.kind === 'labour';
    return (
      <div className="de-lines" role="table" aria-label="Line items">
        {GROUPS.map((group) => this.renderGroup(group))}
        <Alert
          isOpen={Boolean(deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText={deleting ? `Delete ${deleting.group.one}` : 'Delete'}
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>Delete {deleting ? (deleting.item.description || `this ${deleting.group.one}`) : ''}?</strong>
          <p>It comes off this {this.props.doc.noun} right away{this.props.doc.dirty ? ', and your other changes are saved with it' : ''}.</p>
          {labour && deleting.item.time_log_id && (deleting.item.time_log_status === 'approved' ? (
            <p className="de-check-note">Its time log entry is approved, so it stays on the timesheet.</p>
          ) : (
            <label className="de-check">
              <input type="checkbox" checked={this.deleteTimeLog} onChange={(event) => { this.deleteTimeLog = event.target.checked; }} />
              <span>Also delete its time log entry from the timesheet</span>
            </label>
          ))}
        </Alert>
      </div>
    );
  }
}

export default LineItems;
